import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminService } from '../../services/loanService';
import { formatCurrency, formatDate } from '../../utils/helpers';
import LoadingSpinner from '../../components/common/LoadingSpinner';

// ── Helpers ───────────────────────────────────────────────────────────────────

const LOAN_STATUS_META = {
  offer_accepted: { label: 'Awaiting Disbursement', color: '#ff9900' },
  disbursed:      { label: 'Disbursed',             color: '#00C896' },
  active:         { label: 'Active',                color: '#00C878' },
};

function Badge({ value }) {
  const m = LOAN_STATUS_META[value] || { label: value, color: '#94a3b8' };
  return (
    <span style={{
      fontSize: '0.65rem', fontWeight: 700, padding: '0.2rem 0.6rem',
      borderRadius: 99, textTransform: 'uppercase', letterSpacing: '0.05em',
      background: `${m.color}18`, color: m.color, border: `1px solid ${m.color}30`,
    }}>{m.label}</span>
  );
}

function TypeBadge({ type }) {
  const color = type === 'auto' ? '#5B8EFF' : '#818CF8';
  return (
    <span style={{
      fontSize: '0.6rem', fontWeight: 700, padding: '0.15rem 0.5rem',
      borderRadius: 99, textTransform: 'uppercase',
      background: `${color}18`, color, border: `1px solid ${color}30`,
    }}>{type === 'auto' ? 'Auto (Paystack)' : 'Manual'}</span>
  );
}

// ── Detail Modal ──────────────────────────────────────────────────────────────

function LoanDetailModal({ loan, onClose }) {
  if (!loan) return null;

  const sectionTitle = (label) => (
    <div style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--primary)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.75rem', paddingBottom: '0.4rem', borderBottom: '1px solid var(--border)' }}>
      {label}
    </div>
  );

  const field = (label, value) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
      <span style={{ fontSize: '0.68rem', color: 'var(--text-3)', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.05em' }}>{label}</span>
      <span style={{ fontSize: '0.875rem', color: 'var(--text-1)', fontWeight: 500 }}>{value || '—'}</span>
    </div>
  );

  const grid = (children) => (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem 1.5rem', marginBottom: '1.5rem' }}>
      {children}
    </div>
  );

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem',
    }} onClick={onClose}>
      <div style={{
        background: 'var(--surface-2)', borderRadius: '16px', border: '1px solid var(--border)',
        width: '100%', maxWidth: 820, maxHeight: '90vh', overflow: 'auto', padding: '2rem',
      }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.75rem' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700 }}>{loan.application_number}</h2>
            <p style={{ margin: '0.25rem 0 0', fontSize: '0.8rem', color: 'var(--text-2)' }}>Read-only — disbursement view</p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.25rem', color: 'var(--text-3)' }}>✕</button>
        </div>

        {sectionTitle('Applicant')}
        {grid(<>
          {field('Full Name', loan.applicant_name)}
          {field('Email', loan.applicant_email)}
          {field('Phone', loan.applicant_phone)}
        </>)}

        {sectionTitle('Loan Offer')}
        {grid(<>
          {field('Approved Amount', formatCurrency(loan.approved_amount))}
          {field('Net Disbursement', formatCurrency(loan.net_disbursement))}
          {field('Processing Fee', formatCurrency(loan.processing_fee))}
          {field('Interest Rate', loan.interest_rate ? `${loan.interest_rate}% p.m.` : null)}
          {field('Tenure', loan.tenure_months ? `${loan.tenure_months} months` : null)}
          {field('Monthly Installment', formatCurrency(loan.monthly_installment))}
          {field('Offer Accepted', formatDate(loan.offer_accepted_at))}
        </>)}

        {sectionTitle('Bank Details')}
        {grid(<>
          {field('Bank Name', loan.bank_name)}
          {field('Account Number', loan.account_number)}
          {field('Account Name', loan.account_name)}
          {field('Bank Code', loan.bank_code)}
        </>)}

        {sectionTitle('Approval Trail')}
        {grid(<>
          {field('Maker', loan.maker_name)}
          {field('Maker Date', formatDate(loan.maker_recommended_at))}
          {field('Checker', loan.checker_name)}
          {field('Checker Date', formatDate(loan.checker_approved_at))}
        </>)}

        {loan.disbursement_record && (<>
          {sectionTitle('Disbursement Record')}
          {grid(<>
            {field('Reference', loan.disbursement_record.reference)}
            {field('Type', loan.disbursement_record.disbursement_type === 'auto' ? 'Auto (Paystack)' : 'Manual Transfer')}
            {field('Disbursed By', loan.disbursement_record.disbursed_by_name)}
            {field('Disbursed At', formatDate(loan.disbursement_record.disbursed_at))}
          </>)}
        </>)}
      </div>
    </div>
  );
}

// ── Manual Disburse Modal ─────────────────────────────────────────────────────

function ManualDisburseModal({ loan, onClose, onSubmit, isPending }) {
  const [notes, setNotes] = useState('');
  const [file, setFile] = useState(null);
  const fileRef = useRef();

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!file) return;
    const fd = new FormData();
    fd.append('evidence', file);
    if (notes) fd.append('notes', notes);
    onSubmit(fd);
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem',
    }} onClick={onClose}>
      <div style={{
        background: 'var(--surface-2)', borderRadius: '16px', border: '1px solid var(--border)',
        width: '100%', maxWidth: 620, padding: '2rem',
      }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1.25rem' }}>
          <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 700 }}>Manual Disbursement</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.25rem', color: 'var(--text-3)' }}>✕</button>
        </div>

        <div style={{ background: 'var(--primary-light)', border: '1px solid rgba(0,200,150,0.2)', borderRadius: '10px', padding: '0.875rem', marginBottom: '1.25rem' }}>
          <div style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-1)' }}>{loan.applicant_name}</div>
          <div style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--primary)', marginTop: '0.25rem' }}>{formatCurrency(loan.net_disbursement)}</div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-2)', marginTop: '0.25rem' }}>
            {loan.bank_name} — {loan.account_number}
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.4rem', color: 'var(--text-2)' }}>
              Evidence of Transfer <span style={{ color: '#FF4757' }}>*</span>
            </label>
            <div
              onClick={() => fileRef.current?.click()}
              style={{
                border: `2px dashed ${file ? 'var(--primary)' : 'var(--border)'}`,
                borderRadius: '10px', padding: '1.25rem', textAlign: 'center',
                cursor: 'pointer', transition: 'border-color .15s',
                background: file ? 'var(--primary-light)' : 'transparent',
              }}
            >
              <div style={{ fontSize: '1.25rem', marginBottom: '0.4rem' }}>{file ? '✓' : '↑'}</div>
              <div style={{ fontSize: '0.8rem', color: file ? 'var(--primary)' : 'var(--text-2)', fontWeight: file ? 600 : 400 }}>
                {file ? file.name : 'Click to upload receipt / proof of transfer'}
              </div>
              <div style={{ fontSize: '0.7rem', color: 'var(--text-3)', marginTop: '0.25rem' }}>PDF, JPG, PNG — max 10 MB</div>
            </div>
            <input
              ref={fileRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              style={{ display: 'none' }}
              onChange={e => setFile(e.target.files[0] || null)}
            />
          </div>

          <div style={{ marginBottom: '1.25rem' }}>
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.4rem', color: 'var(--text-2)' }}>Notes (optional)</label>
            <textarea
              className="form-input"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={2}
              placeholder="Any notes about this transfer..."
              style={{ resize: 'vertical' }}
            />
          </div>

          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button type="button" className="btn btn-ghost" style={{ flex: 1 }} onClick={onClose}>Cancel</button>
            <button
              type="submit"
              className="btn btn-primary"
              style={{ flex: 2 }}
              disabled={!file || isPending}
            >
              {isPending ? 'Disbursing…' : 'Confirm Manual Disburse'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Auto Disburse Confirm Modal ───────────────────────────────────────────────

function AutoDisburseModal({ loan, onClose, onConfirm, isPending }) {
  const hasBank = loan.bank_code && loan.account_number;
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem',
    }} onClick={onClose}>
      <div style={{
        background: 'var(--surface-2)', borderRadius: '16px', border: '1px solid var(--border)',
        width: '100%', maxWidth: 580, padding: '2rem',
      }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1.25rem' }}>
          <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 700 }}>Confirm Auto Disbursement</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.25rem', color: 'var(--text-3)' }}>✕</button>
        </div>

        {!hasBank ? (
          <div className="alert alert-error" style={{ marginBottom: '1rem' }}>
            <span>⚠</span>
            <span>This applicant's KYC record is missing a bank code or account number. Use manual disburse instead.</span>
          </div>
        ) : (
          <>
            <div style={{ background: 'var(--primary-light)', border: '1px solid rgba(0,200,150,0.2)', borderRadius: '10px', padding: '0.875rem', marginBottom: '1.25rem' }}>
              <div style={{ fontSize: '0.7rem', color: 'var(--text-3)', textTransform: 'uppercase', fontWeight: 600, marginBottom: '0.5rem' }}>Transfer Details</div>
              <div style={{ display: 'grid', gap: '0.5rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
                  <span style={{ color: 'var(--text-2)' }}>Recipient</span>
                  <span style={{ fontWeight: 600 }}>{loan.applicant_name}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
                  <span style={{ color: 'var(--text-2)' }}>Bank</span>
                  <span style={{ fontWeight: 600 }}>{loan.bank_name}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
                  <span style={{ color: 'var(--text-2)' }}>Account</span>
                  <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{loan.account_number}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem', borderTop: '1px solid var(--border)', paddingTop: '0.5rem', marginTop: '0.25rem' }}>
                  <span style={{ color: 'var(--text-2)', fontWeight: 600 }}>Amount</span>
                  <span style={{ fontWeight: 700, color: 'var(--primary)', fontSize: '1rem' }}>{formatCurrency(loan.net_disbursement)}</span>
                </div>
              </div>
            </div>

            <div className="alert alert-info" style={{ marginBottom: '1.25rem' }}>
              <span>ℹ</span>
              <span>Funds will be transferred instantly via Paystack from the lender's account. This action cannot be undone.</span>
            </div>
          </>
        )}

        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button className="btn btn-ghost" style={{ flex: 1 }} onClick={onClose}>Cancel</button>
          <button
            className="btn btn-primary"
            style={{ flex: 2 }}
            disabled={!hasBank || isPending}
            onClick={onConfirm}
          >
            {isPending ? 'Processing…' : 'Confirm Auto Disburse'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function DisbursementOfficerDashboard() {
  const qc = useQueryClient();
  const [tab, setTab] = useState('pending');
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [detailLoan, setDetailLoan] = useState(null);
  const [autoTarget, setAutoTarget] = useState(null);
  const [manualTarget, setManualTarget] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['disbursement-queue', tab],
    queryFn: () => adminService.getDisbursementQueue(tab).then(r => r.data.data),
  });

  const flash = (message, isError = false) => {
    setMsg(''); setErr('');
    if (isError) setErr(message); else setMsg(message);
    setTimeout(() => { setMsg(''); setErr(''); }, 5000);
  };

  const autoMutation = useMutation({
    mutationFn: (loanId) => adminService.autoDisburse(loanId),
    onSuccess: (r) => {
      setAutoTarget(null);
      flash(r.data.message || 'Loan auto-disbursed successfully.');
      qc.invalidateQueries({ queryKey: ['disbursement-queue'] });
    },
    onError: (e) => {
      setAutoTarget(null);
      flash(e.response?.data?.message || 'Auto disbursement failed.', true);
    },
  });

  const manualMutation = useMutation({
    mutationFn: ({ loanId, formData }) => adminService.manualDisburse(loanId, formData),
    onSuccess: (r) => {
      setManualTarget(null);
      flash(r.data.message || 'Loan manually disbursed successfully.');
      qc.invalidateQueries({ queryKey: ['disbursement-queue'] });
    },
    onError: (e) => {
      setManualTarget(null);
      flash(e.response?.data?.message || 'Manual disbursement failed.', true);
    },
  });

  const loans = data || [];

  const tabStyle = (active) => ({
    padding: '0.5rem 1.25rem',
    borderRadius: '8px',
    border: 'none',
    cursor: 'pointer',
    fontSize: '0.875rem',
    fontWeight: active ? 700 : 400,
    background: active ? 'var(--primary)' : 'transparent',
    color: active ? '#fff' : 'var(--text-2)',
    transition: 'all .15s',
  });

  return (
    <div>
      <div className="page-header">
        <h1>Disbursement Queue</h1>
        <p>Disburse approved loans to applicants — auto via Paystack or manually with evidence</p>
      </div>

      {msg && (
        <div className="alert alert-success" style={{ marginBottom: '1rem' }}>
          <span>✓</span><span>{msg}</span>
        </div>
      )}
      {err && (
        <div className="alert alert-error" style={{ marginBottom: '1rem' }}>
          <span>⚠</span><span>{err}</span>
        </div>
      )}

      <div className="alert alert-info" style={{ maxWidth: 720, marginBottom: '1.5rem' }}>
        <span>ℹ</span>
        <span>
          Only loans approved by both maker and checker, where the applicant has accepted the offer, appear here.
          Once disbursed, the loan is locked — you cannot disburse it again.
        </span>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.25rem', background: 'var(--card-bg)', padding: '0.4rem', borderRadius: '10px', border: '1px solid var(--border)', width: 'fit-content' }}>
        <button style={tabStyle(tab === 'pending')} onClick={() => setTab('pending')}>Pending Disbursement</button>
        <button style={tabStyle(tab === 'disbursed')} onClick={() => setTab('disbursed')}>Disbursed</button>
      </div>

      <div className="card" style={{ padding: 0 }}>
        {isLoading ? (
          <div style={{ padding: '2rem' }}><LoadingSpinner /></div>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Loan #</th>
                  <th>Applicant</th>
                  <th>Net Amount</th>
                  <th>Bank / Account</th>
                  <th>Maker</th>
                  <th>Checker</th>
                  <th>Status</th>
                  {tab === 'disbursed' && <th>Type</th>}
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {loans.length === 0 ? (
                  <tr>
                    <td colSpan={9} style={{ textAlign: 'center', color: 'var(--text-3)', padding: '2.5rem' }}>
                      {tab === 'pending' ? 'No loans awaiting disbursement.' : 'No disbursed loans yet.'}
                    </td>
                  </tr>
                ) : loans.map(loan => (
                  <tr key={loan.id}>
                    <td style={{ fontFamily: 'monospace', fontSize: '0.8rem', fontWeight: 600 }}>{loan.application_number}</td>
                    <td>
                      <div style={{ fontSize: '0.875rem', fontWeight: 500 }}>{loan.applicant_name}</div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-3)' }}>{loan.applicant_email}</div>
                    </td>
                    <td style={{ fontWeight: 700, color: 'var(--primary)' }}>{formatCurrency(loan.net_disbursement)}</td>
                    <td style={{ fontSize: '0.8rem' }}>
                      <div>{loan.bank_name || <span style={{ color: 'var(--text-3)' }}>—</span>}</div>
                      <div style={{ fontFamily: 'monospace', color: 'var(--text-3)' }}>{loan.account_number || '—'}</div>
                    </td>
                    <td style={{ fontSize: '0.8rem', color: 'var(--text-2)' }}>{loan.maker_name || '—'}</td>
                    <td style={{ fontSize: '0.8rem', color: 'var(--text-2)' }}>{loan.checker_name || '—'}</td>
                    <td><Badge value={loan.status} /></td>
                    {tab === 'disbursed' && (
                      <td>
                        {loan.disbursement_record
                          ? <TypeBadge type={loan.disbursement_record.disbursement_type} />
                          : '—'}
                      </td>
                    )}
                    <td>
                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                        <button
                          className="btn btn-ghost"
                          style={{ fontSize: '0.75rem', padding: '0.3rem 0.6rem' }}
                          onClick={() => setDetailLoan(loan)}
                        >
                          Details
                        </button>
                        {tab === 'pending' && !loan.disbursed && (
                          <>
                            <button
                              className="btn btn-primary"
                              style={{ fontSize: '0.75rem', padding: '0.3rem 0.7rem' }}
                              onClick={() => setAutoTarget(loan)}
                            >
                              Auto Disburse
                            </button>
                            <button
                              className="btn"
                              style={{ fontSize: '0.75rem', padding: '0.3rem 0.7rem', background: 'var(--sidebar-bg)', border: '1px solid var(--border)', color: 'var(--text-1)' }}
                              onClick={() => setManualTarget(loan)}
                            >
                              Manual Disburse
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modals */}
      {detailLoan && <LoanDetailModal loan={detailLoan} onClose={() => setDetailLoan(null)} />}

      {autoTarget && (
        <AutoDisburseModal
          loan={autoTarget}
          onClose={() => setAutoTarget(null)}
          onConfirm={() => autoMutation.mutate(autoTarget.id)}
          isPending={autoMutation.isPending}
        />
      )}

      {manualTarget && (
        <ManualDisburseModal
          loan={manualTarget}
          onClose={() => setManualTarget(null)}
          onSubmit={(fd) => manualMutation.mutate({ loanId: manualTarget.id, formData: fd })}
          isPending={manualMutation.isPending}
        />
      )}
    </div>
  );
}
